import { TabsProps } from '@praetorian-chariot/ui';
import { useMemo } from 'react';

import { SeedType } from '@/sections/seeds/types';

/**
 * Seed counts for different seed types
 * Note: Still used by useSeedData.ts for isAccountEmpty calculation
 */
export interface SeedCounts {
  tld: number;
  domain: number;
  cidr: number;
  ipv4: number;
  ipv6: number;
  webapplication: number;
}

/**
 * Get tab configuration for seeds section
 *
 * @returns Tab configuration for Tabs component with static labels (no counts)
 *
 * @example
 * ```tsx
 * const tabOptions = getSeedTabs();
 * ```
 */
export const getSeedTabs = (): TabsProps['tabs'] => {
  return [
    { value: 'domain', label: 'Domains' },
    { value: 'ip', label: 'CIDR Ranges' },
    { value: 'preseed', label: 'Preseeds' },
    { value: 'webapplication', label: 'Web Applications' },
  ];
};

/**
 * Custom hook to get seed tabs configuration with memoization
 *
 * @returns Memoized tab options
 *
 * @example
 * ```tsx
 * const tabOptions = useSeedTabs();
 * ```
 */
export const useSeedTabs = (): TabsProps['tabs'] => {
  return useMemo(() => getSeedTabs(), []);
};

/**
 * Get the selected tab from tab options
 *
 * @param tabOptions - Available tab options
 * @param activeTab - Current active tab value
 * @returns Selected tab or first tab as fallback
 */
export const getSelectedTab = (
  tabOptions: TabsProps['tabs'],
  activeTab: SeedType
) => {
  return tabOptions.find(tab => tab.value === activeTab) || tabOptions[0];
};
